import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Scheme } from '@keaze/web/common/interfaces';

import NextImage from 'next/image';
import Box from '@material-ui/core/Box';
import { useEmblaCarousel } from 'embla-carousel/react';

import { PrevButton, NextButton } from './buttons';
import {
  Carousel as UICarousel,
  ViewPort,
  Container,
  Slide,
} from './carousel.styles';

import {
  Schemes,
  MobileSchemes,
  StyledSchema,
  TitleWrapper,
} from '../header/header.styles';
import { CarouselProps } from './carousel.types';

type RenderScheme = (scheme: Scheme) => JSX.Element;

const renderScheme: RenderScheme = ({ text, color }) => (
  <StyledSchema key={text} withoutTail backgroundColor={color}>
    {text}
  </StyledSchema>
);

export const Carousel: FC<CarouselProps> = memo(
  ({ data, schemes, ...BoxProps }) => {
    const [viewportRef, embla] = useEmblaCarousel({ skipSnaps: false });
    const [slidesInView, setSlidesInView] = useState<number[]>([0]);
    const [prevBtnEnabled, setPrevBtnEnabled] = useState<boolean>(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState<boolean>(false);
    const withNavigation = useMemo(() => data.length > 1, [data]);
    const isPreview = useMemo(() => !!data.length && data[0].width === 0, [
      data,
    ]);

    const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
    const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
    const onSelect = useCallback(() => {
      if (!embla) return;

      setSlidesInView((slidesInView) => [
        ...new Set([...slidesInView, ...embla.slidesInView(true)]).values(),
      ]);
      setPrevBtnEnabled(embla.canScrollPrev());
      setNextBtnEnabled(embla.canScrollNext());
    }, [embla]);

    useEffect(() => {
      if (!embla) return;
      onSelect();
      embla.on('select', onSelect);
    }, [embla, onSelect]);

    return (
      <>
        <Schemes>{schemes.map(renderScheme)}</Schemes>

        <UICarousel {...BoxProps}>
          <ViewPort ref={viewportRef}>
            <Container $isPreview={isPreview}>
              {data.map((image, index) => {
                const inView = slidesInView.indexOf(index) > -1;
                const isFirstSlide = index === 0 && inView;
                const nextImage =
                  image.width === 0 ? (
                    <NextImage
                      src={image.src}
                      alt={image.alt}
                      layout="fill"
                      objectFit="contain"
                    />
                  ) : (
                    <NextImage
                      {...image}
                      placeholder="blur"
                      objectFit="contain"
                    />
                  );

                return (
                  <Slide key={index}>
                    {isFirstSlide && nextImage}
                    {inView && !isFirstSlide && (
                      <img src={image.src} alt={image.alt} />
                    )}
                  </Slide>
                );
              })}
            </Container>
          </ViewPort>
          {withNavigation && (
            <Box>
              <PrevButton onClick={scrollPrev} disabled={!prevBtnEnabled} />
              <NextButton onClick={scrollNext} disabled={!nextBtnEnabled} />
            </Box>
          )}
        </UICarousel>
      </>
    );
  }
);
